import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { IRiskScenarioCatalogDialog } from "./RiskScenarioCatalogDialog";
import { ImportRiskScenarioCatalogModel, RiskScenarioCatalogItemViewModel } from "./RiskScenarioCatalogDialog.types";
import { getEntityTypeOptions } from "../RiskScenarios.hooks";
import { allOptionId } from "../../common/RiskSettings.utils";
import { SelectableCatalogRow, useCatalogEntrySelection, useSelectionWithAllByEntry } from "../../common/CatalogSelection.hooks";

export type { SelectableCatalogRow };

export function useRiskScenarioCatalogDialogDataMapping(props: IRiskScenarioCatalogDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskScenarioCategory, translateEntityType } = useTranslation();
    const { isLoading: riskAreasLoading, riskAreas } = useRiskAreas();
    const { selectedEntries, toggleEntry, onSelectAllChanged, getHeaderCheckBoxState, hasSelection } = useCatalogEntrySelection();
    const riskAreaSelection = useSelectionWithAllByEntry();
    const entityTypeSelection = useSelectionWithAllByEntry();

    const { isLoading, data } = useDotLegalQuery<Array<RiskScenarioCatalogItemViewModel>>("/risk-scenarios/catalog");

    const rows: Array<SelectableCatalogRow> = (data ?? []).map((item) => ({
        entry: item.entry,
        name: item.name,
        description: item.description,
        categoryLabel: translateRiskScenarioCategory(item.category),
        isSelected: selectedEntries.includes(item.entry),
    }));

    const allEntries = rows.map((row) => row.entry);

    const riskAreaOptions = [{ id: allOptionId, name: translateString("allRiskAreas") }, ...(riskAreas ?? [])];
    const entityTypeOptions = [{ id: allOptionId, name: translateString("allTypes") }, ...getEntityTypeOptions(translateEntityType)];

    const importMutation = useDotLegalMutation(importEntries, {
        onSuccess: (response: Result<{ imported: number }>) => {
            props.onImported();
            props.onHideDialog();
            snackbar.show(translateString("riskScenariosImported", { count: response.value().imported }));
        },
    });

    function importEntries() {
        const model: ImportRiskScenarioCatalogModel = {
            items: selectedEntries.map((entry) => ({
                entry,
                riskAreaIds: riskAreaSelection.valuesByEntry[entry] ?? [],
                entityTypes: (entityTypeSelection.valuesByEntry[entry] ?? []).map(Number),
            })),
        };
        return post<{ imported: number }>("/risk-scenarios/catalog/import", model);
    }

    return {
        isLoading,
        rows,
        toggleEntry,
        onSelectAllChanged: () => onSelectAllChanged(allEntries),
        getHeaderCheckBoxState: () => getHeaderCheckBoxState(allEntries),
        riskAreaOptions,
        riskAreasLoading,
        getChosenRiskAreas: riskAreaSelection.getChosen,
        setRiskAreas: riskAreaSelection.set,
        entityTypeOptions,
        getChosenEntityTypes: entityTypeSelection.getChosen,
        setEntityTypes: entityTypeSelection.set,
        hasSelection,
        inProgress: importMutation.isLoading,
        importSelected: () => importMutation.mutateAsync(),
    };
}
